import { CommonModule } from '@angular/common';
import {
    Component,
    InputSignal,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { DateTime } from 'luxon';
import { z } from 'zod';
import { IqTableRateSharedComponent } from '../iq-table-rate-shared/iq-table-rate-shared.component';
import {
    ColumnKey,
    HeaderCol,
    HeaderRow,
    RateType,
    Row,
    dailyLabel,
    getCurrencyLabel,
    setUpRow,
    sortRows,
} from '../models';
import { IqTableRateStateService } from '../state';

const CurrentFuelSchema = z.object({
    id: z.string(),
    tripFuel: z.number(),
    mileFuel: z.number(),
    isCustom: z.boolean(),
    isDaily: z.boolean(),
});
type CurrentFuel = z.infer<typeof CurrentFuelSchema>;

@Component({
    selector: 'iq-table-rate-forecast-35d',
    standalone: true,
    imports: [CommonModule, IqTableRateSharedComponent],
    templateUrl: './iq-table-rate-forecast-35d.component.html',
    styleUrl: './iq-table-rate-forecast-35d.component.scss',
})
export class IqTableRateForecast35dComponent {
    id: InputSignal<string> = input('');

    activeHeaderCol: WritableSignal<{ colKey: ColumnKey; col: HeaderCol }> =
        signal({
            colKey: 'date',
            col: { id: 'date', label: 'Date', sort: 'none' },
        });

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    headerRow: Signal<HeaderRow> = computed(() => {
        const r = setUpRow(this.activeHeaderCol(), this.id());
        return r;
    });

    currentFuel: WritableSignal<CurrentFuel> = signal({});
    spotPayloadSignal: WritableSignal<any> = signal(null);
    contractPayloadSignal: WritableSignal<any> = signal(null);
    rateTypeSignal: WritableSignal<RateType> = signal('SPOT');
    perLoadSignal: WritableSignal<boolean> = signal(true);
    spotRowsSignal: WritableSignal<Promise<Row[]>> = signal(null);
    contractRowsSignal: WritableSignal<Promise<Row[]>> = signal(null);

    rowsSignal: WritableSignal<Promise<Row[]>> = signal(null);
    rows: Signal<Promise<Row[]>> = computed((): Promise<Row[]> => {
        return this.rowsSignal();
    });

    sortedRows: Signal<Promise<Row[]>> = computed(async () => {
        const headerRow = this.headerRow();
        const rows = await this.rows();

        return sortRows(rows, headerRow);
    });
    private _dispatchActions: Record<string, IqEventBusBrokerHandler> = {};

    constructor(private readonly _state: IqTableRateStateService) {
        effect(() => {});
        const handlerOpts = {
            parentId: this.id(),
        };
        this._dispatchActions = this._eventBus.registerMap({
            set35DForecastTableBrokerCarrierSpot: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    this.spotPayloadSignal.set(payload);
                    const data: Row[] = this.getRowData();
                    this.spotRowsSignal.set(Promise.resolve(data));
                    if (this.rateTypeSignal() === 'SPOT') {
                        this.rowsSignal.set(this.spotRowsSignal());
                    }
                },
                opts: {
                    ...handlerOpts,
                },
            },
            set35DForecastTableRateType: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;

                    if (payload.rateType === 'ShipperCarrierContract') {
                        this.rateTypeSignal.set('CONTRACT');
                        const data: Row[] = this.getRowData();
                        this.contractRowsSignal.set(Promise.resolve(data));
                        this.rowsSignal.set(this.contractRowsSignal());
                    } else {
                        this.rateTypeSignal.set('SPOT');
                        const data: Row[] = this.getRowData();
                        this.spotRowsSignal.set(Promise.resolve(data));
                        this.rowsSignal.set(this.spotRowsSignal());
                    }
                },
                opts: {
                    ...handlerOpts,
                },
            },
            set135DForecastTableTripType: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    this.perLoadSignal.set(payload?.perLoad);
                    const data: Row[] = this.getRowData();

                    if (this.rateTypeSignal() === 'SPOT') {
                        this.spotRowsSignal.set(Promise.resolve(data));
                        this.rowsSignal.set(this.spotRowsSignal());
                    }
                    if (this.rateTypeSignal() === 'CONTRACT') {
                        this.contractRowsSignal.set(Promise.resolve(data));
                        this.rowsSignal.set(this.contractRowsSignal());
                    }
                },
                opts: {
                    ...handlerOpts,
                },
            },
            set35DForecastTableShipperCarrierContract: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    this.contractPayloadSignal.set(payload);
                    const data: Row[] = this.getRowData();
                    this.contractRowsSignal.set(Promise.resolve(data));
                    if (this.rateTypeSignal() === 'CONTRACT') {
                        this.rowsSignal.set(this.contractRowsSignal());
                    }
                },
                opts: {
                    ...handlerOpts,
                },
            },
            setTableCurrentFuel: {
                callback: (payload: CurrentFuel) => {
                    if (this.id() !== payload.id) return;
                    this.currentFuel.set(payload);
                    const data: Row[] = this.getRowData();
                    if (this.rateTypeSignal() === 'SPOT') {
                        this.spotRowsSignal.set(Promise.resolve(data));
                        this.rowsSignal.set(this.spotRowsSignal());
                    }
                    if (this.rateTypeSignal() === 'CONTRACT') {
                        this.contractRowsSignal.set(Promise.resolve(data));
                        this.rowsSignal.set(this.contractRowsSignal());
                    }
                },
                opts: {
                    ...handlerOpts,
                },
            },
        });
    }

    getRowData(): Row[] {
        const payload: any =
            this.rateTypeSignal() === 'SPOT'
                ? this.spotPayloadSignal()
                : this.contractPayloadSignal();
        const data: Row[] = [];
        payload?.average?.forEach((rate: any, index: number) => {
            const date = DateTime.fromISO(rate.date, {
                zone: 'utc',
            });
            const useMiles: boolean = !this.perLoadSignal();
            const perLoad: boolean = this.perLoadSignal();

            const high = this.calculateForecastRateWithFuel(
                payload?.high[index].value,
                payload?.high[index].mileage,
                this.currentFuel().mileFuel,
                perLoad,
            );
            const low = this.calculateForecastRateWithFuel(
                payload?.low[index].value,
                payload?.low[index].mileage,
                this.currentFuel().mileFuel,
                perLoad,
            );
            const average = this.calculateForecastRateWithFuel(
                payload?.average[index].value,
                payload?.average[index].mileage,
                this.currentFuel().mileFuel,
                perLoad,
            );

            data.push({
                date: {
                    id: `date-${index}`,
                    label: dailyLabel(date),
                    data: date.toJSDate(),
                },
                high: {
                    id: `high-${index}`,
                    label: getCurrencyLabel(high, useMiles),
                    data: high,
                },
                average: {
                    id: `average-${index}`,
                    label: `**${getCurrencyLabel(average, useMiles)}**`,
                    data: average,
                },
                low: {
                    id: `low-${index}`,
                    label: getCurrencyLabel(low, useMiles),
                    data: low,
                },
            });
        });
        return data;
    }

    private calculateForecastRateWithFuel(
        rate: number,
        mileage: number,
        currentFuel: number,
        perLoad: boolean,
    ) {
        const ratePerMile = rate + currentFuel;
        return perLoad ? ratePerMile * mileage : ratePerMile;
    }

    updateSort({ colKey, col }: { colKey: ColumnKey; col: HeaderCol }) {
        this.activeHeaderCol.set({ colKey, col });
    }

    onDestroy() {
        this._eventBus.unregisterMap(this._dispatchActions);
    }
}
